import { Link, useLocation } from "react-router-dom";
import { Home, Search, Calendar, MessageSquare, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { routes } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";

export const AppFooter = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;
  const [unreadMessages, setUnreadMessages] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setup = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const fetchUnread = async () => {
        const { data: conversations } = await supabase
          .from("direct_conversations")
          .select("id")
          .or(`learner_id.eq.${user.id},coach_id.eq.${user.id}`);
        if (!conversations) return;

        const { count } = await supabase
          .from("direct_messages")
          .select("*", { count: "exact", head: true })
          .in("conversation_id", conversations.map(c => c.id))
          .eq("read", false)
          .neq("sender_id", user.id);

        setUnreadMessages(count || 0);
      };

      fetchUnread();

      channel = supabase
        .channel("footer-messages")
        .on("postgres_changes", { event: "*", schema: "public", table: "direct_messages" }, () => fetchUnread())
        .subscribe();
    };

    setup();
    return () => { if (channel) supabase.removeChannel(channel); };
  }, []);

  const isActive = (path: string) => {
    if (path === "/") return pathname === "/";
    if (path.includes("?")) {
      const [p, q] = path.split("?");
      return pathname === p || (pathname.startsWith(p) && search.includes(q));
    }
    return pathname.startsWith(path);
  };

  const linkClass = (path: string) =>
    cn(
      "flex flex-col items-center justify-center gap-1 transition-colors min-w-[56px] min-h-[48px] p-1",
      isActive(path) ? "text-primary" : "text-muted-foreground hover:text-primary"
    );

  return (
    <footer className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t border-border md:hidden z-50 safe-area-bottom">
      <nav className="flex items-center justify-around px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <Link to={routes.home()} className={linkClass("/")} onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}>
          <Home className="h-5 w-5" />
          <span className="text-[11px] font-medium">{t('footer.home')}</span>
        </Link>
        
        <Link to={routes.browseExperiences()} className={linkClass("/browse?type=experiences")} onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}>
          <Search className="h-5 w-5" />
          <span className="text-[11px] font-medium">{t('footer.browse')}</span>
        </Link>
        
        <Link to={routes.bookings()} className={linkClass("/bookings")} onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}>
          <Calendar className="h-5 w-5" />
          <span className="text-[11px] font-medium">{t('footer.bookings')}</span>
        </Link>
        
        <Link to={routes.messages()} className={cn(linkClass("/messages"), "relative")} onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}>
          <div className="relative">
            <MessageSquare className="h-5 w-5" />
            {unreadMessages > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-2 -right-3 h-4 min-w-4 flex items-center justify-center p-0 text-[10px]"
              >
                {unreadMessages > 9 ? "9+" : unreadMessages}
              </Badge>
            )}
          </div>
          <span className="text-[11px] font-medium">{t('footer.messages')}</span>
        </Link>
        
        <Link to={routes.profile()} className={linkClass("/profile")} onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })}>
          <User className="h-5 w-5" />
          <span className="text-[11px] font-medium">{t('footer.profile')}</span>
        </Link>
      </nav>
    </footer>
  );
};
